// conversationAdapter — pure domain-conversation → UI-`ChatEntry[]` codec,
// plus the `sendTurn` outcome → agent reply reader.
//
// The domain `Conversation` is a flat log of typed entries (`user_message`,
// `agent_message`, `agent_status`, `tool_call`, `tool_call_result`, and the
// closing `agent_error` / `agent_interruption` / `agent_budget_halt` /
// `agent_connection_loss`). The UI
// transcript is a wider sum: `author` (plain text), `agent` (markdown),
// `proposal` (a pointer card), and `system` (a quiet closing-outcome row). This
// codec keeps the two message kinds, surfaces the three closing entries as
// system rows, projects the tool machinery into an ambient trail, and now
// surfaces the agent's own preamble prose too — see the header note:
//
//   • `agent_status` — the agent's preamble prose surfaces as a legible `agent`
//     message (O-7), keyed by its raw messageId so the live bubble and the
//     reloaded entry are one (dedup-by-id), never a dim system row.
//   • `tool_call` / `tool_call_result` — projected into an `agent_activity`
//     trail (TOOL mechanics only): a running step resolved by its result.
//   • the `proposal` ChatEntry has NO domain source — the domain has no
//     "pending proposal" entry; the agent's edits are tool side-effects
//     committed to the event log, not a keyed proposal on the conversation.
//   • closing `agent_error` / `agent_interruption` / `agent_budget_halt` now
//     surface as a `system` row (chat-implementation-design §0). Before Slice 0
//     they were DROPPED to `undefined`, so an agent failure rendered as nothing.
//
// Every message's content is a `MessageContent` block array; only the
// `content_text` blocks carry reader-visible prose, so `textOf` joins those.
// Pure + total: no I/O, no throw.
//
// Decomposed (module-topology) into this entry + a sibling `conversationAdapter/`
// folder; the public import path stays `./conversationAdapter.js`. This entry
// keeps the durable transcript projection (`toChatEntries`) + the settled-reply
// reader (`toReply`); `conversationAdapter/elicitation.ts` holds the elicitation-
// form projection helpers (shared by the durable projection AND the live codec),
// and `conversationAdapter/turnStreamCodec.ts` holds the live turn-stream codec
// (`toTurnStreamEvent`, re-exported below).

export type {} from "./conversationAdapter/citations.js";
export {} from "./conversationAdapter/citations.js";
// `toTurnStreamEvent` lives in the sibling codec module — re-export it directly
// so the public import path stays `./conversationAdapter.js`.
export { toTurnStreamEvent } from "./conversationAdapter/turnStreamCodec.js";
export { toChatEntries, toReply };

import type {
    AgentActivityEntry,
    AgentActivityStep,
    ChatCitation,
    ChatEntry,
    ChatFieldAnswer,
    ExportEntry,
    InformationRequestEntry,
    MaterialEntry,
    ProposalStatus,
    SystemEntryKind,
} from "../components/Chat/types.js";
import { toMessageCitations } from "./conversationAdapter/citations.js";
import {
    toChatAnswer,
    toInformationRequestEntry,
} from "./conversationAdapter/elicitation.js";
import { textOf } from "./conversationAdapter/text.js";
import { toToolStepOutcome } from "./conversationAdapter/toolCallDetail.js";
import {
    queuedEntries,
    waitingChanges,
    waitingEntries,
} from "./conversationAdapter/waiting.js";
import {
    renderElicitationResumeText,
    summarizeElicitationAnswers,
} from "./elicitationAnswerMapper.js";
import { mkDailyCeilingReached, mkUsageLimitReached } from "./externalError.js";
import { limitKind, usageLimitKind } from "./interruption.js";
import { bool, fields, list, num, str } from "./wireShape.js";

// Mirror of `@bilda/app`'s `OPENING_MOVE_SEED_TEXT` (by value — the UI imports
// no app/core). The seed that drives an agent-first opening turn; suppressed
// from the transcript so the agent reads as speaking first.
const OPENING_MOVE_SEED_TEXT =
    "(The Author has opened the conversation. Make your opening move.)";

// Project a completed `create_material_from_source` tool result into a material
// CARD. The tool serialises `{materialId, title, kind, itemCount?, ...}` as its
// output string (server tools/resource.ts); this reads that back total — a
// missing id/title or an unparseable string yields `undefined`, so the card
// simply doesn't render (never a throw, never a half-card). `kind` narrows to
// the closed UI tag set; anything but the freeform sentinel is `course` (the
// product's default authoring shape). The entry id is derived from the tool
// call id so it is a stable, unique React key distinct from the activity trail.
const materialCardFrom = (
    callId: string,
    outcome: ReadonlyMap<string, unknown> | undefined,
): MaterialEntry | undefined => {
    const rawOutput = str(outcome?.get("output"));
    if (rawOutput === undefined) return undefined;
    let parsed: unknown;
    try {
        parsed = JSON.parse(rawOutput);
    } catch {
        return undefined;
    }
    const f = fields(parsed);
    const materialId = str(f?.get("materialId"));
    const title = str(f?.get("title"));
    if (materialId === undefined || title === undefined) return undefined;
    const itemCount = num(f?.get("itemCount"));
    return {
        type: "material",
        id: `${callId}-material`,
        materialId,
        title,
        kind: str(f?.get("kind")) === "freeform" ? "freeform" : "course",
        ...(itemCount !== undefined ? { itemCount } : {}),
    };
};

// Project a completed `export_package` tool result into an export CARD —
// mirrors `materialCardFrom` exactly, one field lighter (an export has no
// kind/itemCount, only a title). The tool serialises `{exportId, downloadUrl,
// title, format, note}` (server tools/resource.ts); `downloadUrl`/`format`/
// `note` are read by the host's own Open/Download handlers, not needed here —
// the card only needs the id + title to render. A missing id/title yields
// `undefined`, same total posture as `materialCardFrom`.
const exportCardFrom = (
    callId: string,
    outcome: ReadonlyMap<string, unknown> | undefined,
): ExportEntry | undefined => {
    const rawOutput = str(outcome?.get("output"));
    if (rawOutput === undefined) return undefined;
    let parsed: unknown;
    try {
        parsed = JSON.parse(rawOutput);
    } catch {
        return undefined;
    }
    const f = fields(parsed);
    const exportId = str(f?.get("exportId"));
    const title = str(f?.get("title"));
    if (exportId === undefined || title === undefined) return undefined;
    return { type: "export", id: `${callId}-export`, exportId, title };
};

// === showConversation value → ChatEntry[] =====================================
// Value shape: `{ conversation: { type, entries: [...] } | { type: deleted,
// prior: {...} } }`. Read the active entries (or the deleted `prior`'s), keep
// the two message kinds.

const toChatEntries = (value: unknown): readonly ChatEntry[] => {
    // The envelope-time sidecar (showConversation's `messageTimes`): a
    // `messageId → recordedAt` list the app folds off the event envelopes,
    // since the domain conversation STATE is timeless. Read it total into a
    // `messageId → epoch-ms` resolver; a message with no folded time (older
    // than a seeded instance's tail) simply isn't in the map, so its label
    // stays absent — the same absent-not-zero posture as `timestampOf`.
    const messageTimes = messageTimesMap(fields(value)?.get("messageTimes"));
    const timeOf = (messageId: string): number | undefined =>
        messageTimes.get(messageId);
    const proposalOutcomes = proposalOutcomesMap(
        fields(value)?.get("proposals"),
    );
    const conversation = fields(fields(value)?.get("conversation"));
    // Deleted conversations carry the pre-delete shape under `prior`.
    const active = conversation?.has("entries")
        ? conversation
        : fields(conversation?.get("prior"));
    const out: ChatEntry[] = [];
    // toolCallId → toolName, populated as tool_call entries stream past (a
    // tool_call always precedes its tool_call_result), so the result branch can
    // recognise a completed `create_material_from_source` and project its card.
    const toolNameById = new Map<string, string>();
    // D50: the turn the log has opened but not yet closed, if any. A turn ends
    // only on a closing entry; one still open when the next user message
    // arrives, or at the end of the log, never finished (the server died
    // mid-turn, or failed before recording why) and gets an `unfinished` row.
    // `bySystem`: opened by the server on its own (a source the
    // conversation waited for settled, FR-3.5). Such a turn still open at
    // the end of the log is running in the background, not unfinished.
    let open:
        | { readonly opener: string; readonly bySystem: boolean }
        | undefined;
    // What changed because of each read batch (PS-61), by batch id.
    const changeByBatch = waitingChanges(active);
    list(active?.get("entries")).forEach((raw, i) => {
        const wire = fields(raw);
        const type = str(wire?.get("type"));
        // Messages sent while the assistant worked, read at a step: ordinary
        // messages from then on, then what changed because of them.
        if (type === "waiting_batch") {
            const batchId = str(wire?.get("batchId")) ?? `batch-${i}`;
            if (open === undefined) open = { opener: batchId, bySystem: false };
            out.push(
                ...waitingEntries(
                    wire?.get("messages"),
                    { type: "read" },
                    timeOf,
                ),
            );
            const summary = changeByBatch.get(batchId);
            if (summary !== undefined)
                out.push({
                    type: "waiting_change",
                    id: `${batchId}-change`,
                    summary,
                });
            return;
        }
        if (type === "user_message" && open !== undefined)
            out.push(unfinishedRow(open.opener));
        if (type !== undefined && CLOSING_ENTRY_TYPES.has(type))
            open = undefined;
        else if (type === "user_message" || open === undefined)
            open = {
                opener:
                    str(wire?.get("messageId")) ??
                    str(wire?.get("targetMessageId")) ??
                    `entry-${i}`,
                bySystem: type === "source_settled_notice",
            };
        if (type === "tool_call") {
            const callId = str(wire?.get("toolCallId"));
            const targetId = str(wire?.get("targetMessageId"));
            const name = str(wire?.get("toolName"));
            if (callId !== undefined && name !== undefined) {
                toolNameById.set(callId, name);
            }
            if (
                callId !== undefined &&
                targetId !== undefined &&
                name !== undefined
            ) {
                const activityId = `${targetId}-activity`;
                const existing = out.find(
                    (entry): entry is AgentActivityEntry =>
                        entry.type === "agent_activity" &&
                        entry.id === activityId,
                );
                const step = {
                    type: "tool_running" as const,
                    id: callId,
                    name,
                };
                if (existing === undefined) {
                    out.push({
                        type: "agent_activity",
                        id: activityId,
                        steps: [step],
                    });
                } else {
                    out.splice(out.indexOf(existing), 1, {
                        ...existing,
                        steps: [...existing.steps, step],
                    });
                }
            }
            return;
        }
        if (type === "tool_call_result") {
            const callId = str(wire?.get("toolCallId"));
            // Resolve a running step ONLY on an explicit, recognised outcome:
            // `tool_call_completed` → completed (check, + F124 detail),
            // `tool_call_failed` → failed (warning, + reason). Any other/
            // unreadable outcome leaves the step `tool_running` — a NEUTRAL
            // spark, never a fabricated failure: a warning must mean a genuine
            // `tool_call_failed` and nothing else (dogfood 2026-09-01, Task 4).
            const outcome = toToolStepOutcome(fields(wire?.get("outcome")));
            const ok = outcome?.type === "completed";
            if (callId !== undefined && outcome !== undefined) {
                for (const [index, entry] of out.entries()) {
                    if (entry.type !== "agent_activity") continue;
                    if (
                        !entry.steps.some(
                            (step) =>
                                step.type === "tool_running" &&
                                step.id === callId,
                        )
                    )
                        continue;
                    out[index] = {
                        ...entry,
                        steps: entry.steps.map((step) =>
                            step.type === "tool_running" && step.id === callId
                                ? {
                                      type: "tool_done",
                                      id: step.id,
                                      name: step.name,
                                      outcome,
                                  }
                                : step,
                        ),
                    };
                    break;
                }
            }
            // A completed `create_material_from_source` leaves a first-class
            // material CARD in the transcript (chat-as-live-work-surface
            // §Axis 2): the tool's output carries the created material's id +
            // title + kind, so the card is projected from the ALREADY-logged
            // tool result — no new domain event (O-6). The tool call still
            // shows in the ambient activity trail above; the card is the
            // navigable artifact, same dual-surface as a proposal.
            if (
                callId !== undefined &&
                ok &&
                toolNameById.get(callId) === "create_material_from_source"
            ) {
                const card = materialCardFrom(
                    callId,
                    fields(wire?.get("outcome")),
                );
                if (card !== undefined) out.push(card);
            }
            // A completed `export_package` leaves the same kind of first-class
            // pointer card (F-E/Dm6) — mirrors the material branch above,
            // keyed on the export tool's name instead.
            if (
                callId !== undefined &&
                ok &&
                toolNameById.get(callId) === "export_package"
            ) {
                const card = exportCardFrom(
                    callId,
                    fields(wire?.get("outcome")),
                );
                if (card !== undefined) out.push(card);
            }
            return;
        }
        const entry = mkEntry(fields(raw), i, timeOf);
        if (entry !== undefined) out.push(entry);
    });
    // A turn the server is still running (#100: the page reloaded or lost its
    // connection mid-turn) is in progress, not unfinished.
    const turnRunning = bool(fields(value)?.get("turnRunning"));
    if (open !== undefined && !open.bySystem && !turnRunning)
        out.push(unfinishedRow(open.opener));
    // Elicitation markers live in the conversation's out-of-band artifact
    // accumulator, not in its linear entries. Project them after the
    // transcript so replay does not lose typed clarification rounds.
    const artifacts = list(active?.get("artifacts"));
    const answered = new Set(
        artifacts
            .map((raw) => fields(raw))
            .filter((raw) => str(raw?.get("type")) === "information_answer")
            .map((raw) => str(raw?.get("requestMessageId")))
            .filter((id): id is string => id !== undefined),
    );
    // The text of each answered request's resume message (F97). LEGACY only:
    // new resume messages carry the structural marker (dropped in `mkEntry`).
    const elicitationEchoTexts = new Set<string>();
    // Build the request entries FIRST, keyed by id, so the answer projection can
    // resolve field labels for its structured read-back (F97) regardless of
    // artifact order (an answer may precede its request in the accumulator).
    const requestEntriesById = new Map<string, InformationRequestEntry>();
    for (const raw of artifacts) {
        const artifact = fields(raw);
        if (str(artifact?.get("type")) !== "information_request") continue;
        const id = str(artifact?.get("messageId"));
        if (id === undefined) continue;
        const entry = toInformationRequestEntry(
            id,
            fields(artifact?.get("request")),
            answered.has(id) ? { type: "answered" } : { type: "pending" },
        );
        if (entry !== undefined) requestEntriesById.set(id, entry);
    }
    // F113: fold each answered form + its answers into ONE card. Resolve every
    // answer's structured read-back against its request and NEST it on the request
    // entry (`answers`) BEFORE the ordering loop below, so the entry already
    // carries its answers when pushed. A matched, non-empty answer is nested here
    // (and emits no separate submission entry below); a dangling answer (no
    // matching request) or an empty read-back keeps its own quiet submission line.
    const nestedRequestIds = new Set<string>();
    for (const raw of artifacts) {
        const artifact = fields(raw);
        if (str(artifact?.get("type")) !== "information_answer") continue;
        const requestId = str(artifact?.get("requestMessageId"));
        if (requestId === undefined) continue;
        const request = requestEntriesById.get(requestId);
        if (request === undefined) continue;
        const answer = fields(artifact?.get("answer"));
        const chatAnswers = list(answer?.get("answers"))
            .map(toChatAnswer)
            .filter((a): a is ChatFieldAnswer => a !== undefined);
        // F97: the turn RESUMED with these answers as a durable author
        // `user_message` (see the chat module's supply_information effect); record its text for
        // the fallback matcher below.
        elicitationEchoTexts.add(
            renderElicitationResumeText(request, chatAnswers),
        );
        const summary = summarizeElicitationAnswers(request, chatAnswers);
        if (summary.length === 0) continue;
        requestEntriesById.set(requestId, { ...request, answers: summary });
        nestedRequestIds.add(requestId);
    }
    for (const raw of artifacts) {
        const artifact = fields(raw);
        const type = str(artifact?.get("type"));
        if (type === "information_request") {
            const id = str(artifact?.get("messageId"));
            if (id === undefined) continue;
            const entry = requestEntriesById.get(id);
            if (entry !== undefined) out.push(entry);
        } else if (type === "information_answer") {
            const requestId = str(artifact?.get("requestMessageId"));
            if (requestId === undefined) continue;
            // The read-back is nested on the request card (F113) — emit no
            // separate submission entry.
            if (nestedRequestIds.has(requestId)) continue;
            const answer = fields(artifact?.get("answer"));
            const chatAnswers = list(answer?.get("answers"))
                .map(toChatAnswer)
                .filter((a): a is ChatFieldAnswer => a !== undefined);
            // A dangling answer (no matching request) or an empty read-back
            // degrades to a quiet "answered" line.
            out.push({
                type: "information_submission",
                id: `${requestId}-answer`,
                requestId,
                answers: chatAnswers,
            });
        } else if (type === "plan_marker") {
            const id = str(artifact?.get("messageId"));
            const plan = fields(artifact?.get("plan"));
            const steps = list(plan?.get("steps"))
                .map((step, index) => {
                    const s = fields(step);
                    const title = str(s?.get("title"));
                    if (title === undefined) return undefined;
                    return {
                        type:
                            index % 3 === 0
                                ? "edit"
                                : index % 3 === 1
                                  ? "add"
                                  : "verify",
                        id: `${id ?? "plan"}-${index}`,
                        description: title,
                    } as const;
                })
                .filter(
                    (step): step is NonNullable<typeof step> =>
                        step !== undefined,
                );
            if (id !== undefined && steps.length > 0)
                out.push({ type: "plan", id, steps });
        } else if (type === "proposal_marker") {
            const id = str(artifact?.get("messageId"));
            // Carry the aggregate id so Review can resolve and focus the
            // proposal's target material.
            const proposalId = str(artifact?.get("proposalId"));
            const outcome =
                proposalId === undefined
                    ? undefined
                    : proposalOutcomes.get(proposalId);
            if (id !== undefined)
                out.push({
                    type: "proposal",
                    id,
                    proposalId,
                    // A proposal edits one element.
                    summary: [{ type: "edited", count: 1 }],
                    rationale: outcome?.rationale ?? "",
                    // The marker does not say how well it is backed: show
                    // nothing rather than guess.
                    grounding: { type: "unsupported" },
                    status: outcome?.status ?? { type: "pending" },
                    ...(outcome?.materialTitle === undefined
                        ? {}
                        : { materialTitle: outcome.materialTitle }),
                });
        }
    }
    // Legacy fallback: drop an unmarked resume message (logged before the F113
    // marker existed) by exact text match against its answers.
    const deEchoed = out.filter(
        (entry) =>
            !(entry.type === "author" && elicitationEchoTexts.has(entry.text)),
    );
    return [
        ...attachCitations(
            settleDanglingToolSteps(orderByLogSequence(deEchoed, value)),
            value,
        ),
        ...queuedEntries(active, timeOf),
    ];
};

// The entry types that close a turn (core's `isClosingEntry`, by value: the UI
// imports no core).
const CLOSING_ENTRY_TYPES: ReadonlySet<string> = new Set([
    "agent_message",
    "agent_interruption",
    "agent_error",
    "agent_budget_halt",
    "agent_connection_loss",
]);

// The row for a turn the log never closed (D50), keyed by the opener it belongs
// to, like the other closing rows.
const unfinishedRow = (opener: string): ChatEntry => ({
    type: "system",
    id: `${opener}-unfinished`,
    kind: { type: "unfinished" },
});

// F126: settle any `tool_running` step left standing in the DURABLE projection.
// A reloaded/at-rest transcript is a COMPLETE historical record with no live
// turn behind it, so a step still `tool_running` here is never actually in
// flight — it is a DANGLING tool_use: a `tool_call` the log recorded whose
// matching `tool_call_result` never landed (the interruption+continuation
// cluster — abort is checked between rounds, so an interrupted turn can commit a
// `CallTool` without its close; F118). Left untouched it renders as a perpetual
// running spark + present-tense "Läser materialet…". Settle each to the
// NON-SPINNING terminal state a completed call carries — `tool_done` (ok) → ✓
// "Läste materialet" — never a fabricated `failed`/warning (the durable
// discipline elsewhere in this file: a warning means a genuine `tool_call_failed`
// and nothing else). The LIVE trail (`foldTurnStreamEvent`) keeps its honest
// running spark mid-turn; this governs only the settled projection. Pure + total
// — a transcript with no lingering running step is returned unchanged.
const settleDanglingToolSteps = (
    entries: readonly ChatEntry[],
): readonly ChatEntry[] =>
    entries.map(
        (entry): ChatEntry =>
            entry.type !== "agent_activity" ||
            !entry.steps.some((step) => step.type === "tool_running")
                ? entry
                : {
                      ...entry,
                      steps: entry.steps.map(
                          (step): AgentActivityStep =>
                              step.type === "tool_running"
                                  ? {
                                        type: "tool_done",
                                        id: step.id,
                                        name: step.name,
                                        outcome: { type: "completed" },
                                    }
                                  : step,
                      ),
                  },
    );

// F115: order the combined transcript by the durable event-log APPEND
// SEQUENCE — the one causal, monotonic order that unifies the two disjoint
// projection phases (the linear `entries`, then the out-of-band timeless
// `artifacts`). Without this a material card from a LATER turn (built in the
// linear phase) floats above an elicitation form answered in an EARLIER turn
// (projected at the artifact tail); the sidecar ordinal restores causal order,
// the SAME order the live transcript appends in.
//
// The sort is STABLE and the sidecar OPTIONAL: an absent `logSequence` (older
// payloads, unit fixtures) leaves every ordinal unresolved, so the carry-forward
// fallback keeps `entries` in their exact input order — a behaviour-preserving
// no-op. An entry with no ordinal of its own (a closing `system` row, a material/
// export card, or an event older than a seeded snapshot's tail) inherits the
// ordinal of the entry it currently follows, so it stays glued to its causal
// neighbour rather than jumping.
const orderByLogSequence = (
    entries: readonly ChatEntry[],
    value: unknown,
): readonly ChatEntry[] => {
    const ordinals = logSequenceMap(fields(value)?.get("logSequence"));
    if (ordinals.size === 0) return entries;
    // Carry-forward keys: resolve each entry's own ordinal, else inherit the
    // last-seen one (initialised below the smallest real ordinal so a leading
    // unresolved entry — e.g. a pre-seed-tail message — sorts to the front).
    let carry = -1;
    const keyed = entries.map((entry, input) => {
        const own = logKeyOf(entry);
        const resolved = own === undefined ? undefined : ordinals.get(own);
        if (resolved !== undefined) carry = resolved;
        return { entry, order: resolved ?? carry, input };
    });
    // Array.prototype.sort is stable (ES2019+), so equal `order` keeps input
    // order; the `input` tiebreak makes the intent explicit and total.
    return keyed
        .sort((a, b) => a.order - b.order || a.input - b.input)
        .map((k) => k.entry);
};

// The stable id an entry joins to the `logSequence` sidecar on — mirroring how
// showConversation keyed each conversation event. Message / request / plan /
// proposal entries key by their own id (which IS the event's `messageId`); an
// answered-form submission keys by the request's id (its owning `messageId`);
// an activity trail keys by its first tool call's id (a `toolCallId`). Every
// other entry (closing `system` rows, material/export cards) has no direct key
// and rides the carry-forward fallback — for a card that means the ordinal of
// the tool-activity trail it always follows.
const logKeyOf = (entry: ChatEntry): string | undefined => {
    switch (entry.type) {
        case "author":
        case "agent":
        case "information_request":
        case "plan":
        case "proposal":
            return entry.id;
        case "information_submission":
            return entry.requestId;
        case "agent_activity":
            return entry.steps[0]?.id;
        default:
            return undefined;
    }
};

// Read the `logSequence` sidecar (a `{ key, ordinal }[]`) into a
// `key → ordinal` map. Total: a row missing its key or a non-numeric ordinal is
// skipped. First-write-wins is already applied app-side, so a duplicate key here
// only ever repeats the same ordinal.
const logSequenceMap = (raw: unknown): Map<string, number> => {
    const map = new Map<string, number>();
    for (const rawRow of list(raw)) {
        const row = fields(rawRow);
        const key = str(row?.get("key"));
        const ordinal = num(row?.get("ordinal"));
        if (key === undefined || ordinal === undefined) continue;
        if (!map.has(key)) map.set(key, ordinal);
    }
    return map;
};

// Fold the conversation's `citation_marker` artifacts (cite-A's
// `toMessageCitations`, same `value`) onto their owning agent messages (cite-B).
// A källhänvisning always names a SPECIFIC part (owner refinement), so a
// whole-source marker (absent `locator`) is dropped here — it never becomes an
// inline marker; the source's own title link covers "open the whole source".
// The surviving markers are numbered 1..N in the conversation's own order, so
// the inline footnote number and the ReferencesPanel row always agree. The
// numbered subset is grouped by `messageId` and attached to the matching agent
// entry; a message with no specific-part citation keeps `citations` absent.
const attachCitations = (
    entries: readonly ChatEntry[],
    value: unknown,
): readonly ChatEntry[] => {
    const byMessage = new Map<string, ChatCitation[]>();
    let ordinal = 0;
    for (const citation of toMessageCitations(value)) {
        const { locator } = citation;
        if (locator === undefined) continue; // drop the whole-source marker
        ordinal += 1;
        const forMessage = byMessage.get(citation.messageId) ?? [];
        forMessage.push({
            id: citation.id,
            index: ordinal,
            resourceId: citation.resourceId,
            sourceTitle: citation.sourceTitle,
            locator,
            quote: citation.quote,
        });
        byMessage.set(citation.messageId, forMessage);
    }
    if (byMessage.size === 0) return entries;
    return entries.map((entry) => {
        if (entry.type !== "agent") return entry;
        const citations = byMessage.get(entry.id);
        return citations === undefined ? entry : { ...entry, citations };
    });
};

// Read the `messageTimes` sidecar (a `{ messageId, recordedAt }[]`) into a
// `messageId → epoch-ms` map. Total: a row missing either field, or an
// unparseable `recordedAt`, is skipped — never a NaN or zero instant. `recordedAt`
// is the ISO-8601 envelope stamp; `Date.parse` is a pure static parse (no clock),
// so this stays inside the pure codec.
const messageTimesMap = (raw: unknown): Map<string, number> => {
    const map = new Map<string, number>();
    for (const rawRow of list(raw)) {
        const row = fields(rawRow);
        const messageId = str(row?.get("messageId"));
        const iso = str(row?.get("recordedAt"));
        if (messageId === undefined || iso === undefined) continue;
        const ms = Date.parse(iso);
        if (!Number.isNaN(ms)) map.set(messageId, ms);
    }
    return map;
};

// Read the `proposals` sidecar (a `{ proposalId, status, materialTitle?,
// rationale? }[]`:
// where each proposal the conversation raised stands) into a
// `proposalId → outcome` map. Total: a row without an id is skipped, and an
// unknown or missing status reads as pending.
type ProposalOutcome = {
    readonly status: ProposalStatus;
    readonly materialTitle: string | undefined;
    readonly rationale: string | undefined;
};

const proposalStatusOf = (raw: unknown): ProposalStatus => {
    switch (str(fields(raw)?.get("type"))) {
        case "accepted":
            return { type: "accepted" };
        case "rejected":
            return { type: "rejected" };
        default:
            return { type: "pending" };
    }
};

const proposalOutcomesMap = (raw: unknown): Map<string, ProposalOutcome> => {
    const map = new Map<string, ProposalOutcome>();
    for (const rawRow of list(raw)) {
        const row = fields(rawRow);
        const proposalId = str(row?.get("proposalId"));
        if (proposalId === undefined) continue;
        map.set(proposalId, {
            status: proposalStatusOf(row?.get("status")),
            materialTitle: str(row?.get("materialTitle")),
            rationale: str(row?.get("rationale")),
        });
    }
    return map;
};

// Project a message's recorded instant into the UI's epoch-ms `timestamp` (drives
// the ticking relative-time label). Prefer the `messageTimes` sidecar (keyed by
// this entry's `messageId`), then fall back to an entry-local numeric `timestamp`
// or ISO-8601 `recordedAt` — and OMIT the field entirely when none resolve, the
// same absent-not-zero posture as `suggestionsOf`. The sidecar is the live seam:
// the domain conversation STATE is timeless (the instant lives on the event
// envelope), so showConversation folds `messageTimes` and this joins it in.
const timestampOf = (
    entry: ReadonlyMap<string, unknown> | undefined,
    messageId: string,
    timeOf: (messageId: string) => number | undefined,
): { readonly timestamp?: number } => {
    const folded = timeOf(messageId);
    if (folded !== undefined) return { timestamp: folded };
    const numeric = num(entry?.get("timestamp"));
    if (numeric !== undefined) return { timestamp: numeric };
    const iso = str(entry?.get("recordedAt"));
    if (iso !== undefined) {
        const ms = Date.parse(iso);
        if (!Number.isNaN(ms)) return { timestamp: ms };
    }
    return {};
};

const mkEntry = (
    entry: ReadonlyMap<string, unknown> | undefined,
    index: number,
    timeOf: (messageId: string) => number | undefined,
): ChatEntry | undefined => {
    const type = str(entry?.get("type"));
    // The message's own id doubles as the React key and the `messageTimes` join
    // key; derive it once so the timestamp join and the entry id never diverge.
    const messageId = str(entry?.get("messageId")) ?? `entry-${index}`;
    switch (type) {
        case "user_message": {
            const text = textOf(entry?.get("content"));
            // Suppress the opening-move seed: an agent-first opening rides on a
            // synthetic user turn (Conversation invariant #4 — only a user
            // message opens a turn), and that seed is internal, not the
            // Author's words. Dropping it keeps the transcript reading as the
            // agent speaking first. Mirrors the app's OPENING_MOVE_SEED_TEXT by
            // value — @bilda/client imports zero @bilda/app, same posture as the
            // wire-shape readers.
            if (text === OPENING_MOVE_SEED_TEXT) return undefined;
            // F113: a form-resume message is internal too — the answers already
            // show inside the answered form card. Suppressed by its structural
            // marker, never by its text.
            if (str(entry?.get("resumesRequestMessageId")) !== undefined)
                return undefined;
            const referencedIds = list(entry?.get("resourceRefs")).flatMap(
                (ref) => str(fields(ref)?.get("id")) ?? [],
            );
            return {
                type: "author",
                id: messageId,
                text,
                ...(referencedIds.length > 0 ? { referencedIds } : {}),
                ...timestampOf(entry, messageId, timeOf),
            };
        }
        case "agent_message": {
            const markdown = textOf(entry?.get("content"));
            // Skip empty (F43b): a turn that ends on a form (or any other
            // typed epilogue) has no reply prose of its own, so the server
            // still commits a closing `AgentMessageSent` with empty content
            // under the SAME id as the epilogue artifact. Rendering it would
            // add a blank bubble with no value regardless of what shares its
            // id — same "skip empty" idiom as `agent_status` below.
            if (markdown.trim() === "") return undefined;
            return {
                type: "agent",
                id: messageId,
                markdown,
                // The flat `suggestedResponses` pill projection is RETIRED (LANE
                // S3). A turn's hand-off is the typed epilogue — an
                // `InformationRequestEntry` (durable, projected from the
                // artifact marker below) or a transient `ActionOfferEntry` from
                // the side-effect track. A plain reply carries no move-ahead.
                ...timestampOf(entry, messageId, timeOf),
            };
        }
        // The three CLOSING entries a turn can end on without an agent message.
        // Before Slice 0 (chat-implementation-design §0) all three fell through
        // to the `undefined` drop below, so an interrupted / failed / cost-split
        // turn rendered as a SILENT GAP — an agent failure the author never saw.
        // Now each surfaces as a quiet `system` row (ChatMessage renders the
        // icon + word). They carry `targetMessageId` (the opener they close), not
        // their own `messageId`, so the id is derived + suffixed to stay a unique
        // React key distinct from the opener's author row.
        //
        // NOTE: the `refused` SystemEntryKind has NO case here on purpose — a
        // refused request is a CLIENT-side outcome (the fetch never completed,
        // so nothing was committed for it to project). It is an optimistic
        // transcript row decided in chatDecider (turn_cut_short), never a
        // durable domain entry; on reload the transcript reflects what the server
        // actually recorded (a real reply, or a durable agent_error), per
        // events-are-truth.
        case "agent_interruption":
            return {
                type: "system",
                id: closingId(entry, index, "interrupted"),
                kind: { type: "interrupted" },
            };
        case "agent_error":
            return {
                type: "system",
                id: closingId(entry, index, "failed"),
                kind: failureKind(entry?.get("reason")),
            };
        case "agent_budget_halt":
            return {
                type: "system",
                id: closingId(entry, index, "budget-halted"),
                kind: { type: "budget_halted" },
            };
        case "agent_connection_loss":
            return {
                type: "system",
                id: closingId(entry, index, "connection-lost"),
                kind: { type: "connection_lost", messageSaved: true },
            };
        case "agent_status": {
            // O-7: a durable agent status note carries the agent's own preamble
            // prose. It projects as a legible AGENT message, keyed by its RAW
            // messageId — the SAME id the live `status` fold upserts its bubble
            // under (turnStream), so the live-then-reload handoff dedupes by id
            // (no double render) instead of leaving a dim `-status` system row.
            const text = str(entry?.get("status"));
            if (text === undefined || text === "") return undefined;
            return {
                type: "agent",
                id: messageId,
                markdown: text,
                ...timestampOf(entry, messageId, timeOf),
            };
        }
        case "tool_call":
        case "tool_call_result":
            // Projected in toChatEntries so results can resolve their matching
            // running step; this branch is defensive for direct callers.
            return undefined;
        // The server opening a turn on its own (a source settled): not the
        // Author's words and not a reply, so it has no row of its own. A
        // turn picked up after a restart (#424) goes on in the same rows.
        case "source_settled_notice":
        case "turn_resumption":
            return undefined;
        // The designed default: an entry this version has no rendering for
        // shows as one generic line, never a silent gap or raw data.
        default:
            return { type: "unknown", id: `${messageId}-unknown` };
    }
};

// The id for a closing entry's system row. The domain entry keys itself by the
// opener it closes (`targetMessageId`), so a bare pass-through would collide with
// the opener's own author row; the per-kind suffix keeps the React key unique.
const closingId = (
    entry: ReadonlyMap<string, unknown> | undefined,
    index: number,
    suffix: string,
): string =>
    `${str(entry?.get("targetMessageId")) ?? `entry-${index}`}-${suffix}`;

// Map a wire `ToolFailureReason` into the failed-family `SystemEntryKind`.
// A context-window overflow (O-4) and an out-of-credits account get their OWN
// kinds (neither is retryable as-is); every other known failure is a generic
// `failed` (the raw wire detail is developer English, so it never reaches the
// notice — D18). A reason this parser does not recognise is the designed
// default, `unknown`, carrying the raw value.
const knownFailureCategories: ReadonlySet<string> = new Set([
    "timeout",
    "network",
    "provider_busy",
    "permission_denied",
    "invalid_arguments",
    "source_content_missing",
    "adapter_crashed",
    "other",
]);
const failureKind = (raw: unknown): SystemEntryKind => {
    const reason = fields(raw);
    const type = str(reason?.get("type"));
    if (type === "unknown_tool" || type === "interrupted_by_restart")
        return { type: "failed" };
    if (type === "turn_attempts_exhausted")
        return { type: "gave_up", cause: "restarts" };
    if (type === "turn_time_exhausted")
        return { type: "gave_up", cause: "time" };
    if (type === "no_reply") return { type: "no_reply" };
    const category = str(reason?.get("category"));
    if (category === "context_window_exceeded")
        return { type: "context_window_exceeded" };
    if (category === "quota_exhausted") return { type: "budget_used_up" };
    if (category === "usage_limit_reached")
        return usageLimitKind(mkUsageLimitReached(reason?.get("detail")));
    if (category === "daily_ceiling_reached")
        return limitKind(mkDailyCeilingReached(reason?.get("detail")));
    return category !== undefined && knownFailureCategories.has(category)
        ? { type: "failed" }
        : { type: "unknown", cause: String(JSON.stringify(raw)) };
};

// === sendTurn value → the closing turn entry ==================================
// `SendTurnOutput` = `{ userMessageId, outcome, toolCalls, appendedCount }`.
// The `outcome` sum closes the turn: `agent_replied` carries the agent's
// `content` → an `agent` entry; the other outcomes (errored / interrupted /
// budget_halted) carry no prose and are NOT the agent's voice, so they close as
// a quiet `system` row — the same treatment `mkEntry` gives the equivalent
// domain closing entries, so the optimistic reply and the reloaded transcript
// render a failed turn identically (before Slice 0 this path faked an `agent`
// note, which read as the agent speaking about its own failure). There is NO
// landed-proposal equivalent in the real workflow — `TurnResult.landed` is left
// absent.

const toReply = (value: unknown): ChatEntry | undefined => {
    const outcome = fields(fields(value)?.get("outcome"));
    const type = str(outcome?.get("type"));
    if (type === "agent_replied") {
        const markdown = textOf(outcome?.get("content"));
        // Skip empty (F43b): a turn that ends on a form (or any other typed
        // epilogue) commits an `agent_replied` outcome with no prose of its
        // own, under the SAME `agentMessageId` as the epilogue artifact
        // (`information_request` etc.). Yielding "no entry" here means the
        // live path never appends a blank bubble next to that artifact —
        // an empty agent bubble carries no value regardless of what else
        // shares its id.
        if (markdown.trim() === "") return undefined;
        return {
            type: "agent",
            id: str(outcome?.get("agentMessageId")) ?? "agent-reply",
            markdown,
            // The flat `suggestedResponses` projection is RETIRED (LANE S3) —
            // the turn's hand-off is the typed epilogue (ElicitationForm marker
            // / transient ActionOffer), not chips on the reply.
        };
    }
    const targetId =
        str(outcome?.get("targetMessageId")) ??
        str(fields(value)?.get("userMessageId")) ??
        "agent-reply";
    return {
        type: "system",
        id: `${targetId}-close`,
        kind: closingKind(type, outcome?.get("reason")),
    };
};

// Map a non-reply turn OUTCOME to a `SystemEntryKind`. The outcome sum
// (`agent_errored` / `agent_interrupted` / `agent_budget_halted`) mirrors the
// domain closing entries `mkEntry` maps; an unrecognised type is the designed
// default, `unknown`, rather than vanishing.
const closingKind = (
    outcomeType: string | undefined,
    reason: unknown,
): SystemEntryKind => {
    switch (outcomeType) {
        case "agent_interrupted":
            return { type: "interrupted" };
        case "agent_budget_halted":
            return { type: "budget_halted" };
        case "agent_errored":
            return failureKind(reason);
        default:
            return { type: "unknown", cause: outcomeType ?? "missing outcome" };
    }
};
